/**
 * As permissões por pessoa (ADR-0034).
 *
 * Por ora, o catálogo do que se liga (#727): as abas, os assuntos e as ações
 * que a seção Permissões desenha, e o padrão de cada papel da organização. Os
 * nomes de recurso e de ação são os da matriz da API; o teste
 * `apps/api/src/permissoes/__tests__/catalogo.test.mts` reprova se as duas
 * listas divergirem.
 */

import { z } from 'zod';
import { CarteiraSchema, PapelSchema } from './comum.js';

/** Os recursos da matriz de permissões. */
export const RecursoDePermissaoSchema = z.enum([
  'conversa',
  'mensagem',
  'cliente',
  'oportunidade',
  'tarefa',
  'usuario',
  'time',
  'integracao',
  'automacao',
  'integracao_de_dados',
  'lista',
  'indicador',
  'auditoria',
  'configuracao',
  'campo_personalizado',
  'mensagem_pronta',
  'permissao',
  'titular',
]);

export type RecursoDePermissao = z.infer<typeof RecursoDePermissaoSchema>;

/** As ações da matriz. */
export const AcaoDePermissaoSchema = z.enum([
  'ler',
  'criar',
  'editar',
  'remover',
  'atribuir',
  'exportar',
  'ativar',
]);

export type AcaoDePermissao = z.infer<typeof AcaoDePermissaoSchema>;

/** Os papéis que a organização dá às pessoas dela: um recorte do `PapelSchema`. */
export const PapelDaOrganizacaoSchema = PapelSchema.extract([
  'agente',
  'vendedor',
  'gestor',
  'admin_tenant',
]);

export type PapelDaOrganizacao = z.infer<typeof PapelDaOrganizacaoSchema>;

const PreRequisitoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  acao: AcaoDePermissaoSchema,
});

const AcaoDoCatalogoSchema = z.object({
  acao: AcaoDePermissaoSchema,
  /** O texto do toggle. */
  rotulo: z.string(),
  /** A linha de apoio embaixo do toggle. */
  descricao: z.string().nullable(),
  /**
   * O que precisa estar ligado para esta ligar; desligar um deles desliga
   * esta. A de ver do próprio assunto, e às vezes a de outro (responder pede
   * ver as conversas).
   */
  depende_de: z.array(PreRequisitoSchema),
  /** O motivo de aparecer travada (é só da Cliqfy), ou `null`. */
  trava: z.string().nullable(),
});

const AssuntoDoCatalogoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  nome: z.string(),
  cobre: z.string(),
  /** Tem "Até onde vale". Os outros valem para a organização inteira. */
  tem_alcance: z.boolean(),
  acoes: z.array(AcaoDoCatalogoSchema),
});

const PermissaoDoAssuntoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  acoes: z.array(AcaoDePermissaoSchema),
  /** Até onde vale: os mesmos níveis da carteira do vínculo. */
  alcance: CarteiraSchema,
});

export const CatalogoDePermissoesSchema = z.object({
  abas: z.array(
    z.object({
      aba: z.enum(['atendimento', 'comercial', 'organizacao', 'integracoes']),
      rotulo: z.string(),
      assuntos: z.array(AssuntoDoCatalogoSchema),
    }),
  ),
  /** O padrão de cada papel: o que ele pode hoje, e de onde parte um ajuste. */
  padroes: z.array(
    z.object({
      papel: PapelDaOrganizacaoSchema,
      permissoes: z.array(PermissaoDoAssuntoSchema),
    }),
  ),
});

export type CatalogoDePermissoes = z.infer<typeof CatalogoDePermissoesSchema>;
